import { useEffect, useRef, useCallback, useState, type ReactNode } from 'react'
import { EditorState } from 'prosemirror-state'
import { EditorView } from 'prosemirror-view'
import { DOMParser, DOMSerializer } from 'prosemirror-model'
import { baseKeymap } from 'prosemirror-commands'
import { keymap } from 'prosemirror-keymap'
import { history, undo, redo } from 'prosemirror-history'
import { buildKeymap } from 'prosemirror-example-setup'
import { inlineEditHighlightPlugin, inlineEditHighlightKey } from '@/inline-edit/util/highlight-plugin'
import { marked } from 'marked'
import { turndownService } from '../utils/turndown'
import { markdownEditorSchema } from '../prosemirror/schema'
import MarkdownEditorToolbar, { EditorSaveStatusType } from './MarkdownEditorToolbar'
import { TextSizeLabel } from './TextSizeDropdown'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { ReferenceMarkerView } from '../prosemirror/reference-marker-node-view'
import { isReferenceViewerHref, replaceCitationsWithSpans } from '../utils/citation-helpers'
import type { ReferenceInfoByCitationId } from '../utils/citation-helpers'
import './markdown-editor.css'
import './markdown-editor-toolbar.css'

// Helper function to get text style from heading level
const getTextStyleFromHeading = (level: number): TextSizeLabel => {
  switch (level) {
    case 1:
      return TextSizeLabel.HEADING_1
    case 2:
      return TextSizeLabel.HEADING_2
    case 3:
      return TextSizeLabel.HEADING_3
    default:
      return TextSizeLabel.NORMAL_TEXT
  }
}

interface MarkdownEditorProps {
  initialMarkdown: string
  onChange?: (markdown: string) => void
  onBlur?: () => void
  readOnly?: boolean
  showMenu?: boolean
  onEditorReady?: (getEditorView: () => EditorView | null) => void
  saveStatus?: EditorSaveStatusType
  isApplyingEdit?: boolean
  onRetry?: () => void
  onCursorChange?: (textStyle: string) => void
  onFormatChange?: (formatState: Record<string, boolean>) => void
  artifactId?: string
  artifactVersionId?: string
  conversationId?: string
  sessionId?: string
  matterId?: string
  onSelectionChange?: (selection: { text: string; from: number; to: number; head: number } | null) => void
  onFormatAction?: () => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  trackEvent?: <T extends AnalyticsEvent>(event: T, properties?: any) => void
  /** Declarative highlight decoration — MarkdownEditor applies/clears it via ProseMirror plugin */
  highlightDecoration?: { from: number; to: number; className: string } | null
  templateButton?: ReactNode
  /** Reference map for rendering citations — [ref_xxx] markers as badges, [authority](ref_xxx) legal citation links as clickable authority text */
  referenceInfoByCitationId?: ReferenceInfoByCitationId
}

export default function MarkdownEditor({
  initialMarkdown,
  onChange,
  onBlur,
  readOnly = false,
  showMenu = false,
  onEditorReady,
  saveStatus,
  isApplyingEdit,
  onRetry,
  onCursorChange,
  onFormatChange,
  artifactId,
  artifactVersionId,
  conversationId,
  sessionId,
  matterId,
  onSelectionChange,
  onFormatAction,
  trackEvent,
  highlightDecoration,
  templateButton,
  referenceInfoByCitationId,
}: MarkdownEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null)
  const editorViewRef = useRef<EditorView | null>(null)
  const lastExternalMarkdown = useRef<string | undefined>(initialMarkdown)
  const isInternalChange = useRef(false)
  const [editorInitialized, setEditorInitialized] = useState(false)
  const [currentTextStyle, setCurrentTextStyle] = useState<string>(TextSizeLabel.NORMAL_TEXT)
  const [formatState, setFormatState] = useState({
    strongActive: false,
    emActive: false,
    bulletListActive: false,
    orderedListActive: false,
  })

  // Analytics tracking
  const hasTrackedFirstEdit = useRef(false)
  const editorStartTime = useRef<number>(Date.now())

  // Store stable references to callbacks
  const onChangeRef = useRef(onChange)
  const onBlurRef = useRef(onBlur)
  const onCursorChangeRef = useRef(onCursorChange)
  const onFormatChangeRef = useRef(onFormatChange)
  const onSelectionChangeRef = useRef(onSelectionChange)

  // Update refs when props change
  useEffect(() => {
    onChangeRef.current = onChange
    onBlurRef.current = onBlur
    onCursorChangeRef.current = onCursorChange
    onFormatChangeRef.current = onFormatChange
    onSelectionChangeRef.current = onSelectionChange
  }, [onChange, onBlur, onCursorChange, onFormatChange, onSelectionChange])

  // Export the menu creation function and expose editor view
  const getEditorView = useCallback(() => editorViewRef.current, [])

  // Parse HTML to create initial editor state
  const createEditorState = (htmlContent: string) => {
    const container = document.createElement('div')
    container.innerHTML = htmlContent

    const doc = DOMParser.fromSchema(markdownEditorSchema).parse(container)

    const plugins = [
      history(),
      keymap(buildKeymap(markdownEditorSchema)),
      keymap(baseKeymap),
      keymap({
        'Mod-z': undo,
        'Mod-y': redo,
        'Mod-Shift-z': redo,
      }),
      inlineEditHighlightPlugin,
    ]

    // Don't add the default prosemirror menu if we're showing our custom menu

    return EditorState.create({
      doc,
      schema: markdownEditorSchema,
      plugins,
    })
  }

  // Initialize editor only once on mount.
  // Preprocesses citations into spans so ProseMirror picks them up — [ref_xxx] markers as
  // referenceMarker nodes, [authority](ref_xxx) legal citation links as legalCitation marks.
  // If references haven't loaded yet, citations render inertly and update when the
  // external-update effect below detects a referenceInfoByCitationId change.
  useEffect(() => {
    if (!editorRef.current || editorViewRef.current) return

    const md = referenceInfoByCitationId ? replaceCitationsWithSpans(initialMarkdown || '', referenceInfoByCitationId) : initialMarkdown || ''
    const state = createEditorState(marked(md, { gfm: true }) as string)

    const view = new EditorView(editorRef.current, {
      state,
      nodeViews: {
        referenceMarker: (node, view, getPos, decorations) => new ReferenceMarkerView(node, view, getPos, decorations),
      },
      dispatchTransaction(transaction) {
        const newState = view.state.apply(transaction)
        view.updateState(newState)

        // Always check for cursor position changes
        const { $from, from, to } = newState.selection
        const node = $from.parent
        let textStyle: TextSizeLabel | string = TextSizeLabel.NORMAL_TEXT

        if (node.type.name === 'heading') {
          const level = node.attrs.level
          textStyle = getTextStyleFromHeading(level)
        }

        // Detect format states
        // Check if cursor is in a list by traversing up the node tree
        let bulletListActive = false
        let orderedListActive = false

        // Traverse up the node tree to find list nodes
        for (let i = 0; i <= $from.depth; i++) {
          const nodeAtDepth = $from.node(i)
          if (nodeAtDepth.type.name === 'bullet_list') {
            bulletListActive = true
            break
          } else if (nodeAtDepth.type.name === 'ordered_list') {
            orderedListActive = true
            break
          }
        }

        const newFormatState = {
          strongActive:
            from === to
              ? !!markdownEditorSchema.marks.strong.isInSet(newState.storedMarks || $from.marks())
              : newState.doc.rangeHasMark(from, to, markdownEditorSchema.marks.strong),
          emActive:
            from === to
              ? !!markdownEditorSchema.marks.em.isInSet(newState.storedMarks || $from.marks())
              : newState.doc.rangeHasMark(from, to, markdownEditorSchema.marks.em),
          bulletListActive,
          orderedListActive,
        }

        // Update internal states
        setCurrentTextStyle(textStyle)
        setFormatState(newFormatState)

        // Call external callbacks if provided
        if (onCursorChangeRef.current) {
          onCursorChangeRef.current(textStyle)
        }

        if (onFormatChangeRef.current) {
          onFormatChangeRef.current(newFormatState)
        }

        // Notify parent of text selection changes
        if (onSelectionChangeRef.current) {
          if (from !== to) {
            onSelectionChangeRef.current({ text: newState.doc.textBetween(from, to, ' '), from, to, head: newState.selection.head })
          } else {
            onSelectionChangeRef.current(null)
          }
        }

        // Skip for external updates (reference map changes, etc.) — these update the
        // editor visuals but shouldn't trigger auto-save which blocks subsequent ref-map updates.
        if (onChangeRef.current && !readOnly && transaction.docChanged && !transaction.getMeta('externalUpdate')) {
          // Track first edit event for artifacts
          if (artifactId && trackEvent && !hasTrackedFirstEdit.current) {
            hasTrackedFirstEdit.current = true
            trackEvent(AnalyticsEvent.ArtifactEditorFirstEdit, {
              artifactId,
              timeToFirstEdit: Date.now() - editorStartTime.current,
              sessionId,
              conversationId,
              artifactVersionId,
              matterId: matterId ?? 'none',
            })
          }

          // Mark this as an internal change
          isInternalChange.current = true

          // Convert ProseMirror document back to HTML
          const serializer = DOMSerializer.fromSchema(markdownEditorSchema)
          const div = document.createElement('div')
          const fragment = serializer.serializeFragment(newState.doc.content)
          div.appendChild(fragment)

          // Convert HTML back to Markdown
          const markdown = turndownService.turndown(div.innerHTML)

          lastExternalMarkdown.current = markdown
          onChangeRef.current(markdown)

          // Reset the flag after a brief delay
          setTimeout(() => {
            isInternalChange.current = false
          }, 100)
        }
      },
      // Legal citations are marks (editable text), not NodeViews, so click-through to the
      // source viewer is handled here — same behavior as ReferenceMarkerView badges.
      handleClick(view, pos) {
        const legalCitation = view.state.doc
          .resolve(pos)
          .marks()
          .find((mark) => mark.type.name === 'legalCitation')
        if (legalCitation?.attrs.viewerUrl) {
          // noopener severs window.opener; the href guard rejects non-viewer URLs
          // (mark attrs are paste-injectable via parseDOM).
          if (!isReferenceViewerHref(legalCitation.attrs.viewerUrl)) return false
          window.open(legalCitation.attrs.viewerUrl, '_blank', 'noopener,noreferrer')
          return true
        }
        return false
      },
      handleDOMEvents: {
        blur: () => {
          if (onBlurRef.current) onBlurRef.current()
          return false
        },
      },
      editable: () => !readOnly,
    })

    editorViewRef.current = view
    setEditorInitialized(true)

    // Initial format state detection
    const initialState = view.state
    const { $from, from, to } = initialState.selection
    const node = $from.parent
    let textStyle: TextSizeLabel | string = TextSizeLabel.NORMAL_TEXT

    if (node.type.name === 'heading') {
      const level = node.attrs.level
      textStyle = getTextStyleFromHeading(level)
    }

    // Detect format states for initial load
    let initialBulletListActive = false
    let initialOrderedListActive = false

    // Traverse up the node tree to find list nodes
    for (let i = 0; i <= $from.depth; i++) {
      const nodeAtDepth = $from.node(i)
      if (nodeAtDepth.type.name === 'bullet_list') {
        initialBulletListActive = true
        break
      } else if (nodeAtDepth.type.name === 'ordered_list') {
        initialOrderedListActive = true
        break
      }
    }

    const initialFormatState = {
      strongActive:
        from === to
          ? !!markdownEditorSchema.marks.strong.isInSet(initialState.storedMarks || $from.marks())
          : initialState.doc.rangeHasMark(from, to, markdownEditorSchema.marks.strong),
      emActive:
        from === to
          ? !!markdownEditorSchema.marks.em.isInSet(initialState.storedMarks || $from.marks())
          : initialState.doc.rangeHasMark(from, to, markdownEditorSchema.marks.em),
      bulletListActive: initialBulletListActive,
      orderedListActive: initialOrderedListActive,
    }

    setCurrentTextStyle(textStyle)
    setFormatState(initialFormatState)

    // Notify parent that editor is ready
    if (onEditorReady) {
      onEditorReady(getEditorView)
    }

    return () => {
      if (editorViewRef.current) {
        editorViewRef.current.destroy()
        editorViewRef.current = null
      }
    }
  }, []) // No dependencies - only create once

  // Update editor when markdown or reference map changes from external sources.
  const lastReferenceInfoByCitationIdRef = useRef(referenceInfoByCitationId)
  useEffect(() => {
    // Skip if editor isn't ready or the change came from user typing
    if (!editorViewRef.current || isInternalChange.current) return

    // Detect what changed — markdown from parent, or references loading from Redux
    const markdownChanged = initialMarkdown !== undefined && initialMarkdown !== lastExternalMarkdown.current
    const referenceInfoByCitationIdChanged = referenceInfoByCitationId !== lastReferenceInfoByCitationIdRef.current
    if (!markdownChanged && !referenceInfoByCitationIdChanged) return

    // Use latest markdown, falling back to last known if only referenceInfoByCitationId changed
    const md = initialMarkdown ?? lastExternalMarkdown.current ?? ''
    const currentDoc = editorViewRef.current.state.doc
    const preprocessed = referenceInfoByCitationId ? replaceCitationsWithSpans(md, referenceInfoByCitationId) : md
    const newHtml = marked(preprocessed, { gfm: true }) as string
    const container = document.createElement('div')
    container.innerHTML = newHtml
    const newDoc = DOMParser.fromSchema(markdownEditorSchema).parse(container)

    // Only dispatch if the document actually differs (prevents unnecessary re-renders).
    // Tagged as externalUpdate so it doesn't trigger auto-save which blocks subsequent updates.
    if (!currentDoc.eq(newDoc)) {
      const tr = editorViewRef.current.state.tr
        .replaceWith(0, currentDoc.content.size, newDoc.content)
        .setMeta('addToHistory', false)
        .setMeta('externalUpdate', true)
      editorViewRef.current.dispatch(tr)
    }

    // Track what we've seen to avoid re-processing the same inputs
    if (markdownChanged) lastExternalMarkdown.current = initialMarkdown
    lastReferenceInfoByCitationIdRef.current = referenceInfoByCitationId
  }, [initialMarkdown, referenceInfoByCitationId])

  // Update readonly state
  useEffect(() => {
    if (editorViewRef.current) {
      editorViewRef.current.setProps({
        editable: () => !readOnly,
      })
    }
  }, [readOnly])

  // Clipboard copy analytics
  useEffect(() => {
    if (!artifactId || !artifactVersionId) return
    if (!editorViewRef.current) return

    const handleCopy = (e: ClipboardEvent) => {
      e.stopPropagation()
      if (!trackEvent) return
      trackEvent(AnalyticsEvent.ArtifactClipboardCopy, {
        artifactId,
        artifactVersionId,
        sessionId: sessionId ?? '',
        matterId,
      })
    }

    const dom = editorViewRef.current.dom
    dom.addEventListener('copy', handleCopy)

    return () => {
      dom.removeEventListener('copy', handleCopy)
    }
  }, [artifactId, artifactVersionId, trackEvent, sessionId, matterId])

  // Apply/clear highlight decoration declaratively via props
  useEffect(() => {
    const view = editorViewRef.current
    if (!view) return
    if (highlightDecoration) {
      view.dispatch(view.state.tr.setMeta(inlineEditHighlightKey, highlightDecoration))
    } else {
      view.dispatch(view.state.tr.setMeta(inlineEditHighlightKey, null))
    }
  }, [highlightDecoration])

  return (
    <div className="prose-editor-wrapper">
      {showMenu && editorInitialized && (
        <MarkdownEditorToolbar
          editorView={editorViewRef.current}
          schema={markdownEditorSchema}
          saveStatus={saveStatus}
          isApplyingEdit={isApplyingEdit}
          onRetry={onRetry}
          currentTextStyle={currentTextStyle}
          formatState={formatState}
          artifactId={artifactId}
          artifactVersionId={artifactVersionId}
          conversationId={conversationId}
          sessionId={sessionId}
          matterId={matterId}
          onFormatAction={onFormatAction}
          trackEvent={trackEvent}
          templateButton={templateButton}
        />
      )}
      <div className="prose-editor-container bg-white p-2 h-full overflow-y-auto flex justify-center">
        <div ref={editorRef} className={`prose-mirror-editor prose max-w-4xl w-full ${readOnly ? 'pointer-events-none opacity-75' : ''}`} />
      </div>
    </div>
  )
}
